import { useEffect, useRef } from 'react';
import { X, Trophy } from 'lucide-react';

const TIER_COLORS = ['', 'from-amber-400 to-amber-600', 'from-slate-300 to-slate-500', 'from-yellow-400 to-yellow-600', 'from-cyan-400 to-cyan-600', 'from-violet-500 to-violet-700'];
const TIER_NAMES  = ['', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'];

interface TrophyInfo {
  name: string;
  tier: number;
  bonusXp: number;
}

interface Props {
  trophies: TrophyInfo[];
  onClose: () => void;
}

export default function TrophyEarnedModal({ trophies, onClose }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const t = setTimeout(() => onClose(), 8000);
    closeRef.current?.focus();
    return () => clearTimeout(t);
  }, [onClose]);

  const primary = trophies[0];
  if (!primary) return null;

  const tierGrad  = TIER_COLORS[primary.tier] ?? TIER_COLORS[1];
  const tierLabel = TIER_NAMES[primary.tier]  ?? 'Bronze';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden w-full max-w-sm text-center p-8">
        <div className={`absolute top-0 inset-x-0 h-2 bg-gradient-to-r ${tierGrad}`} />

        <button
          ref={closeRef}
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400"
        >
          <X className="h-4 w-4" />
        </button>

        <div className={`flex items-center justify-center w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br ${tierGrad} shadow-lg dark:shadow-black/40`}>
          <Trophy className="h-10 w-10 text-white" />
        </div>

        <p className="text-xs font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-widest mb-1">
          {tierLabel} Trophy Unlocked!
        </p>
        <p className="text-2xl font-black text-gray-900 dark:text-zinc-100 mb-2">{primary.name}</p>

        {trophies.length > 1 && (
          <p className="text-sm text-gray-500 dark:text-zinc-500 mb-2">+{trophies.length - 1} more trophy{trophies.length > 2 ? 'ies' : ''}</p>
        )}

        {primary.bonusXp > 0 && (
          <span className="inline-block bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 text-sm font-bold px-3 py-1 rounded-full mt-1">
            +{primary.bonusXp} XP bonus!
          </span>
        )}

        <button onClick={onClose} className="mt-6 w-full btn-primary py-2.5">
          View Trophy Case
        </button>
      </div>
    </div>
  );
}
